// ============================================================
// Departments Page — Staff Management
// ============================================================

import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Plus, Search, Edit, Trash2, Building2, Lock } from 'lucide-react';
import { useDepartments, useCreateDepartment, useUpdateDepartment, useDeleteDepartment } from '@/hooks/useStaffManagement';
import { PageHeader } from '@/components/navigation/PageHeader';
import { ConfirmationDialog } from '@/components/dialogs/ConfirmationDialog';
import { DepartmentModal } from './components/DepartmentModal';
import { ErrorState } from '@/components/feedback/ErrorState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { usePermission } from '@/hooks/usePermission';
import { PERMISSIONS } from '@/constants/permissions';
import toast from 'react-hot-toast';

export function DepartmentsPage() {
  usePageMeta('Departments | Staff Management', [
    { label: 'Staff Management', path: '/staff-management' },
    { label: 'Departments' },
  ]);

  const { data: departments = [], isLoading, error, refetch } = useDepartments();
  const createMutation = useCreateDepartment();
  const updateMutation = useUpdateDepartment();
  const deleteMutation = useDeleteDepartment();

  const { can } = usePermission();
  const canCreate = can(PERMISSIONS.STAFF_MANAGE_CREATE) || can(PERMISSIONS.STAFF_MANAGE_VIEW);
  const canUpdate = can(PERMISSIONS.STAFF_MANAGE_UPDATE) || can(PERMISSIONS.STAFF_MANAGE_VIEW);
  const canDelete = can(PERMISSIONS.STAFF_MANAGE_DELETE) || can(PERMISSIONS.STAFF_MANAGE_VIEW);

  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [deleteDept, setDeleteDept] = useState(null);

  const filteredDepartments = (departments || []).filter((dept) => {
    if (!search.trim()) return true;
    const query = search.toLowerCase();
    const nameMatch = dept.name?.toLowerCase().includes(query);
    const descMatch = dept.description?.toLowerCase().includes(query);
    return nameMatch || descMatch;
  });

  const handleOpenCreate = () => {
    setEditingDept(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (dept) => {
    const isProtected = String(dept?.name || '').trim().toUpperCase() === 'ADMIN';
    if (isProtected) {
      toast.error('System ADMIN department cannot be modified');
      return;
    }
    setEditingDept(dept);
    setModalOpen(true);
  };

  const handleSubmitModal = async (payload) => {
    try {
      if (editingDept) {
        await updateMutation.mutateAsync({ id: editingDept.id, ...payload });
        toast.success('Department updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Department created successfully');
      }
      setModalOpen(false);
      setEditingDept(null);
    } catch (err) {
      toast.error(err.message || 'Failed to save department');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDept) return;
    const isProtected = String(deleteDept?.name || '').trim().toUpperCase() === 'ADMIN';
    if (isProtected) {
      toast.error('System ADMIN department cannot be deleted');
      setDeleteDept(null);
      return;
    }
    try {
      await deleteMutation.mutateAsync(deleteDept.id);
      toast.success('Department deleted successfully');
      setDeleteDept(null);
    } catch (err) {
      toast.error(err.message || 'Failed to delete department');
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Department Name',
      width: 260,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Building2 size={16} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.5 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 350,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
            {params.value || 'No description provided.'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      renderCell: (params) => {
        const isProtected = String(params.row.name || '').trim().toUpperCase() === 'ADMIN';
        if (isProtected) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', height: '100%', pr: 1 }}>
              <Tooltip title="System Department (Protected)">
                <Chip
                  icon={<Lock size={12} />}
                  label="Protected"
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', borderColor: 'divider' }}
                />
              </Tooltip>
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', height: '100%', gap: 0.5 }}>
            {canUpdate && (
              <Tooltip title="Edit Department">
                <IconButton
                  size="small"
                  onClick={() => handleOpenEdit(params.row)}
                  sx={{ color: 'primary.main' }}
                >
                  <Edit size={16} />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete Department">
                <IconButton
                  size="small"
                  onClick={() => setDeleteDept(params.row)}
                  sx={{ color: 'error.main' }}
                >
                  <Trash2 size={16} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  if (error) {
    return (
      <Box>
        <ErrorState error={error} onRetry={refetch} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Departments"
        subtitle="Manage organizational departments and their descriptions"
        actions={
          canCreate && (
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={handleOpenCreate}
              sx={{ fontWeight: 600 }}
            >
              Add Department
            </Button>
          )
        }
      />

      {/* Filter and Search Bar */}
      <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <TextField
            placeholder="Search departments..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Showing <strong>{filteredDepartments.length}</strong> of <strong>{(departments || []).length}</strong> departments
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden' }}>
        <DataGrid
          autoHeight
          rows={filteredDepartments}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id || Math.random().toString()}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          sx={{
            border: 0,
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      </Box>

      {/* Create / Edit Modal */}
      <DepartmentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        department={editingDept}
        onSubmit={handleSubmitModal}
        loading={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={!!deleteDept}
        onClose={() => setDeleteDept(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Department"
        message={`Are you sure you want to delete the department "${deleteDept?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmColor="error"
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
